import { Icon, IconButton, Stack, Typography } from '@mui/material';
import { useNotifications } from '@schooly/components/notifications';
import { buildTestId, FILE } from '@schooly/constants/test-ids';
import { useFileDropZone } from '@schooly/hooks/use-file-drop-zone';
import {
  ConvertedIcon,
  DeleteIcon,
  DropZoneBlock,
  ImageFormatIcon,
  ImageSizeIcon,
  NewTabIcon,
  Spin,
  SuccessBlock,
} from '@schooly/style';
import { FC, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { downloadFile } from '../../../../utils/downloadFile';
import { TimetableImportFile } from './ImportTimetableModal';

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const XLSX_FORMAT = '.xlsx';
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;

interface ImportFileCardProps {
  onDrop: (file: File) => void;
  isLoading: boolean;
  onClear: () => void;
  fileData: TimetableImportFile | undefined;
  error?: boolean;
}

export const ImportFileCard: FC<ImportFileCardProps> = ({
  onDrop,
  isLoading,
  onClear,
  fileData,
  error: externalError,
}) => {
  const { formatMessage } = useIntl();
  const { showError } = useNotifications();

  const { getRootProps, getInputProps, isDragActive, hasError } = useFileDropZone({
    accept: { [XLSX_MIME]: [XLSX_FORMAT] },
    maxSize: MAX_FILE_SIZE,
    onDrop,
    onError: (message) => showError({ message }),
    errorMessages: {
      fileTooLarge: formatMessage(
        { id: 'dnd-Error-FileIsLarger' },
        { size: `${MAX_FILE_SIZE_MB} MB` },
      ),
      fileInvalidType: formatMessage({ id: 'dnd-Error-InvalidFormat' }, { format: XLSX_FORMAT }),
    },
  });

  const handleDownload = useCallback(() => {
    if (!fileData) return;
    downloadFile(fileData.rawBuffer, fileData.fileName, XLSX_MIME);
  }, [fileData]);

  if (isLoading) {
    return (
      <Stack
        alignItems="flex-start"
        justifyContent="center"
        sx={{
          p: 1.5,
          borderRadius: 1,
          bgcolor: 'background.paper',
          height: 44,
        }}
      >
        <Icon>
          <Spin />
        </Icon>
      </Stack>
    );
  }

  if (fileData) {
    return (
      <SuccessBlock
        direction="row"
        justifyContent="space-between"
        gap={1}
        data-test-id={buildTestId(
          FILE.UPLOADED_FILE_ROW_PREFIX,
          { name: fileData.fileName },
          'name',
        )}
        sx={{ cursor: 'pointer', maxHeight: 44, width: '100%' }}
        onClick={handleDownload}
      >
        <Stack direction="row" gap={1} sx={{ overflow: 'hidden' }}>
          <Icon>
            <ConvertedIcon />
          </Icon>
          <Typography
            className="fileName"
            variant="h3"
            color="inherit"
            flex={1}
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {fileData.fileName}
          </Typography>
          <IconButton className="downloadIcon" size="small" sx={{ visibility: 'hidden' }}>
            <NewTabIcon />
          </IconButton>
        </Stack>
        <IconButton
          className="deleteIcon"
          data-test-id={FILE.DELETE_UPLOADED_FILE_BTN}
          sx={{ visibility: 'hidden' }}
          onClick={(e) => {
            e.stopPropagation();
            onClear();
          }}
        >
          <DeleteIcon />
        </IconButton>
      </SuccessBlock>
    );
  }

  return (
    <DropZoneBlock
      {...getRootProps()}
      active={isDragActive}
      error={hasError || externalError}
      gap={1.5}
    >
      <input {...getInputProps()} />
      <Stack alignItems="center" gap={2.5}>
        <Stack alignItems="center">
          <Typography variant="h3" color="inherit">
            {formatMessage({ id: 'dnd-File' })}
          </Typography>
          <Typography color="inherit">
            {formatMessage(
              { id: 'dnd-BrowseAFile' },
              {
                browse: (
                  <Typography component="span" color="primary.main">
                    {formatMessage({ id: 'dnd-Browse' })}
                  </Typography>
                ),
              },
            )}
          </Typography>
        </Stack>
        <Stack direction="row" gap={2} alignItems="center">
          <Stack direction="row" gap={0.5} alignItems="center">
            <Icon fontSize="small" sx={{ color: 'inherit' }}>
              <ImageSizeIcon />
            </Icon>
            <Typography variant="caption" color="inherit">
              {formatMessage({ id: 'dnd-MaxSize' }, { size: `${MAX_FILE_SIZE_MB} MB` })}
            </Typography>
          </Stack>
          <Stack direction="row" gap={0.5} alignItems="center">
            <Icon fontSize="small" sx={{ color: 'inherit' }}>
              <ImageFormatIcon />
            </Icon>
            <Typography variant="caption" color="inherit">
              {formatMessage({ id: 'dnd-Format' }, { format: XLSX_FORMAT })}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </DropZoneBlock>
  );
};
